import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppShell } from '../contexts/AppShellContext';
import { getWorkflow } from '../services/workflowService';
import { PersistentHeader } from '../components/workflows/PersistentHeader';
import { OverviewTab } from '../components/workflows/tabs/OverviewTab';
import { IdentityTab } from '../components/workflows/tabs/IdentityTab';
import { ConnectionsTab } from '../components/workflows/tabs/ConnectionsTab';
import { LogicTab } from '../components/workflows/tabs/LogicTab';
import { GraphTab } from '../components/workflows/tabs/GraphTab';
import { RunsTab } from '../components/workflows/tabs/RunsTab';
import { ObservabilityTab } from '../components/workflows/tabs/ObservabilityTab';
import { VersionsTab } from '../components/workflows/tabs/VersionsTab';
import { SecurityTab } from '../components/workflows/tabs/SecurityTab';
import { AuditTab } from '../components/workflows/tabs/AuditTab';
import { GraphNode, NodeStatus, WorkflowRun } from '../types/workflow';

const TABS = [
  'Overview',
  'Identity',
  'Connections',
  'Graph',
  'Logic',
  'Observability',
  'Runs',
  'Versions',
  'Security',
  'Audit',
] as const;
type Tab = typeof TABS[number];

export function WorkflowDetailPage() {
  const { workflowId } = useParams<{ workflowId: string }>();
  const { setTopbar } = useAppShell();
  const navigate = useNavigate();
  const workflow = workflowId ? getWorkflow(workflowId) : undefined;

  const [activeTab, setActiveTab] = useState<Tab>('Overview');
  const [running, setRunning] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [nodeStatus, setNodeStatus] = useState<Record<string, NodeStatus>>({});
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null);
  const [extraRuns, setExtraRuns] = useState<WorkflowRun[]>([]);
  const runCounter = useRef(0);

  useEffect(() => {
    setTopbar(workflow ? workflow.name : 'Workflow', workflow ? workflow.description : '');
  }, [setTopbar, workflow]);

  if (!workflow) {
    return (
      <div className="p-8">
        <button onClick={() => navigate('/workflows')} className="text-sm text-teal-700 hover:underline">
          ← Back to Workflow Library
        </button>
        <div className="mt-6 p-6 bg-white rounded-xl border border-trim text-sm text-muted">Workflow not found.</div>
      </div>
    );
  }

  function handleRun() {
    if (running) return;
    setRunning(true);
    setActiveTab('Graph');
    setToast('Run started — watch the graph');

    const nodes = workflow!.graph.nodes;
    const stepMs = 350;
    const next: Record<string, NodeStatus> = {};

    nodes.forEach((n, idx) => {
      setTimeout(() => {
        next[n.id] = 'running';
        setNodeStatus({ ...next });
      }, idx * stepMs);
      setTimeout(() => {
        next[n.id] = 'success';
        setNodeStatus({ ...next });
      }, idx * stepMs + stepMs - 60);
    });

    const total = nodes.length * stepMs + 400;
    setTimeout(() => {
      runCounter.current += 1;
      const startedMs = Date.now() - total;
      const newRun: WorkflowRun = {
        run_id: `LIVE-${runCounter.current.toString().padStart(4, '0')}`,
        trace_id: `tr_live_${Date.now().toString(16)}`,
        correlation_id: `cor_live_${runCounter.current}`,
        started_at: new Date(startedMs).toISOString(),
        completed_at: new Date().toISOString(),
        duration_ms: total,
        status: 'succeeded',
        trigger_source: 'manual',
        initiated_by: 'demo@opsai.local',
        retry_count: 0,
        node_timeline: nodes.map((n, idx) => ({
          node_id: n.id,
          node_label: n.label,
          started_at: new Date(startedMs + idx * stepMs).toISOString(),
          duration_ms: stepMs,
          status: 'success',
        })),
        logs: [
          { ts: new Date(startedMs).toISOString(), level: 'info', message: 'Live run started from header' },
          { ts: new Date().toISOString(), level: 'info', message: 'Run succeeded' },
        ],
        payloads: { input: { trigger: 'manual' }, output: { ok: true } },
      };
      setExtraRuns(prev => [newRun, ...prev]);
      setRunning(false);
      setNodeStatus({});
      setToast(`Run ${newRun.run_id} succeeded — see Runs tab`);
      setTimeout(() => setToast(null), 2200);
    }, total);
  }

  function handleAction(label: string) {
    setToast(`${label} — coming soon`);
    setTimeout(() => setToast(null), 2000);
  }

  function renderTab() {
    switch (activeTab) {
      case 'Overview':      return <OverviewTab workflow={workflow!} />;
      case 'Identity':      return <IdentityTab workflow={workflow!} />;
      case 'Connections':   return <ConnectionsTab workflow={workflow!} />;
      case 'Logic':         return <LogicTab workflow={workflow!} />;
      case 'Graph':         return (
        <GraphTab
          workflow={workflow!}
          nodeStatusOverride={nodeStatus}
          onNodeSelect={setSelectedNode}
          selectedNodeId={selectedNode?.id ?? null}
        />
      );
      case 'Runs':          return <RunsTab workflow={workflow!} extraRuns={extraRuns} />;
      case 'Observability': return <ObservabilityTab workflow={workflow!} />;
      case 'Versions':      return <VersionsTab workflow={workflow!} />;
      case 'Security':      return <SecurityTab workflow={workflow!} />;
      case 'Audit':         return <AuditTab workflow={workflow!} />;
    }
  }

  return (
    <div className="h-full flex flex-col overflow-hidden bg-wash">
      <div className="bg-white border-b border-trim flex-shrink-0">
        <div className="px-8 pt-4">
          <button onClick={() => navigate('/workflows')} className="text-xs text-teal-700 hover:underline">
            ← Workflow Library
          </button>
        </div>
        <PersistentHeader workflow={workflow} onRun={handleRun} running={running} onAction={handleAction} />
        <div className="px-8 flex gap-1 overflow-x-auto">
          {TABS.map(t => (
            <button
              key={t}
              onClick={() => setActiveTab(t)}
              className="px-3 py-2.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap"
              style={{
                borderColor: activeTab === t ? '#063F5A' : 'transparent',
                color: activeTab === t ? '#063F5A' : '#595A60',
              }}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-8 py-6">{renderTab()}</div>

      {toast && (
        <div className="fixed bottom-6 right-6 bg-ink text-white px-4 py-2.5 rounded-lg text-sm shadow-lg z-50">
          {toast}
        </div>
      )}
    </div>
  );
}
